<script setup lang="ts">
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-vue-next'

export interface TableColumn {
  key: string
  label: string
  sortable?: boolean
  align?: 'left' | 'right'
  headerClass?: string
}

const props = defineProps<{
  columns: TableColumn[]
  sort?: string
  direction?: 'asc' | 'desc'
  compact?: boolean
  tableFixed?: boolean
}>()

const emit = defineEmits<{
  sort: [key: string, direction: 'asc' | 'desc']
}>()

function handleSort(col: TableColumn) {
  if (!col.sortable) return
  const dir = props.sort === col.key && props.direction === 'asc' ? 'desc' : 'asc'
  emit('sort', col.key, dir)
}
</script>

<template>
  <div class="overflow-x-auto">
    <table :class="['w-full text-sm', tableFixed && 'table-fixed']">
      <thead>
        <tr class="border-border border-b">
          <th
            v-for="col in columns"
            :key="col.key"
            :class="[
              'text-muted-foreground font-medium',
              compact ? 'px-4 py-3' : 'px-6 py-3',
              col.align === 'right' ? 'text-right' : 'text-left',
              col.sortable && 'cursor-pointer select-none hover:text-foreground',
              col.headerClass,
            ]"
            @click="handleSort(col)"
          >
            <span class="inline-flex items-center gap-1">
              {{ col.label }}
              <template v-if="col.sortable">
                <ChevronUp v-if="sort === col.key && direction === 'asc'" class="size-3.5 shrink-0" />
                <ChevronDown v-else-if="sort === col.key && direction === 'desc'" class="size-3.5 shrink-0" />
                <ChevronsUpDown v-else class="size-3.5 shrink-0 opacity-40" />
              </template>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <slot />
      </tbody>
    </table>
  </div>
</template>
